import { BanxaBulkPricesResponse, CachedBanxaPrices } from './apiService';

/**
 * Cache service for Banxa bulk prices
 * Implements 3-minute cache expiry with coin-only keys
 */
export class BanxaCacheService {
  private static readonly CACHE_PREFIX = 'banxa_prices_';
  private static readonly CACHE_DURATION = 3 * 60 * 1000; // 3 minutes in milliseconds

  /**
   * Generate cache key based on coin code only
   */
  private static generateCacheKey(coinCode: string): string {
    return `${this.CACHE_PREFIX}${coinCode.toUpperCase()}`;
  }

  /**
   * Check if cached data is still valid
   */
  private static isCacheValid(timestamp: number): boolean {
    const now = Date.now();
    return (now - timestamp) < this.CACHE_DURATION;
  }

  /**
   * Get cached Banxa prices for a specific coin
   */
  static getCachedPrices(coinCode: string): BanxaBulkPricesResponse | null {
    try {
      const cacheKey = this.generateCacheKey(coinCode);
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) {
        return null;
      }

      const parsedCache: CachedBanxaPrices = JSON.parse(cachedData);
      
      if (!this.isCacheValid(parsedCache.timestamp)) {
        // Cache expired, remove it
        this.clearCache(coinCode);
        return null;
      }

      return parsedCache.data;
    } catch (error) {
      console.error('Error reading cached Banxa prices:', error);
      // Clear potentially corrupted cache
      this.clearCache(coinCode);
      return null;
    }
  }

  /**
   * Cache Banxa prices for a specific coin
   */
  static setCachedPrices(coinCode: string, data: BanxaBulkPricesResponse): void {
    try {
      const cacheKey = this.generateCacheKey(coinCode);
      const cacheData: CachedBanxaPrices = {
        data,
        timestamp: Date.now(),
        coinCode: coinCode.toUpperCase()
      };

      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      console.error('Error caching Banxa prices:', error);
    }
  }

  /**
   * Clear cached prices for a specific coin
   */
  static clearCache(coinCode: string): void {
    try {
      const cacheKey = this.generateCacheKey(coinCode);
      localStorage.removeItem(cacheKey);
    } catch (error) {
      console.error('Error clearing Banxa cache:', error);
    }
  }

  /**
   * Clear all Banxa caches
   */
  static clearAllCaches(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.CACHE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing all Banxa caches:', error);
    }
  }

  /**
   * Get cache expiry time for a specific coin
   */
  static getCacheExpiryTime(coinCode: string): number | null {
    try {
      const cacheKey = this.generateCacheKey(coinCode);
      const cachedData = localStorage.getItem(cacheKey);
      
      if (!cachedData) {
        return null;
      }

      const parsedCache: CachedBanxaPrices = JSON.parse(cachedData);
      return parsedCache.timestamp + this.CACHE_DURATION;
    } catch (error) {
      console.error('Error getting cache expiry time:', error);
      return null;
    }
  }

  /**
   * Check if cache exists for a specific coin (regardless of validity)
   */
  static hasCache(coinCode: string): boolean {
    try {
      const cacheKey = this.generateCacheKey(coinCode);
      return localStorage.getItem(cacheKey) !== null;
    } catch (error) {
      console.error('Error checking cache existence:', error);
      return false;
    }
  }
}
